import { Link } from "@tanstack/react-router";
import { Fragment } from "react";
import { Separator } from "@/components/ui/separator";
import { useUser } from "@/utils";
import { type NavItem, visibleSections } from "./nav-items";

/* focus-ring-inset, not focus-ring: the rail sits inside an `overflow-x-clip`
   ancestor, which clips a ring drawn at `outline-offset: 2px`. */
const ITEM_CLASSES =
  "flex items-center gap-3 rounded-md border-l-2 border-transparent px-3 py-2 text-sm " +
  "text-muted-foreground hover:bg-accent hover:text-accent-foreground focus-ring-inset";

/* Styled off the `data-status` TanStack's Link already writes, which also carries
   the `aria-current`. Border and weight, not colour alone. */
const ACTIVE_CLASSES =
  "data-[status=active]:border-primary data-[status=active]:bg-accent " +
  "data-[status=active]:font-semibold data-[status=active]:text-accent-foreground";

function NavLink({
  item,
  onNavigate,
}: {
  item: NavItem;
  onNavigate?: () => void;
}) {
  const Icon = item.icon;
  const content = (
    <>
      <Icon className="size-4 flex-none" aria-hidden="true" />
      <span className="min-w-0 truncate">{item.label}</span>
    </>
  );

  if (item.href) {
    return (
      <a
        href={item.href}
        className={ITEM_CLASSES}
        onClick={onNavigate}
        // A label, not the visible text plus an sr-only span: the accname algorithm
        // joins text nodes without a space, giving "Documentation(opens in a new tab)".
        {...(item.external
          ? {
              target: "_blank",
              rel: "noopener",
              "aria-label": `${item.label} (opens in a new tab)`,
            }
          : {})}
      >
        {content}
      </a>
    );
  }

  return (
    // A real <Link>, never <button onClick={navigate}>: ConfirmOnExit intercepts
    // same-origin anchors with a hash, and a button slips past that guard.
    <Link
      to={item.to}
      // Without this, "/" matches every route and two items look active at once.
      activeOptions={{ exact: item.to === "/" }}
      className={`${ITEM_CLASSES} ${ACTIVE_CLASSES}`}
      onClick={onNavigate}
    >
      {content}
    </Link>
  );
}

/**
 * The nav items, shared by the desktop rail and the mobile drawer.
 *
 * Heading-free on purpose: this renders ahead of <main>, so a section heading
 * would put an h2 in front of every page's h1. The <nav> label names the whole.
 */
export function NavList({ onNavigate }: { onNavigate?: () => void }) {
  const { data: user } = useUser();

  return (
    <>
      {visibleSections(user).map((section, index) => (
        <Fragment key={section.id}>
          {index > 0 && <Separator className="my-2" />}
          <ul className="flex flex-col gap-1">
            {section.items.map((item) => (
              <li key={item.label}>
                <NavLink item={item} onNavigate={onNavigate} />
              </li>
            ))}
          </ul>
        </Fragment>
      ))}
    </>
  );
}
